/**
 * @fileoverview Application root - routing, theme, auth flow, and context providers.
 *
 * Provider hierarchy (order matters):
 *   ErrorBoundary → AuthProvider → FeatureProvider → DataProvider → App (Router)
 *
 * Auth flow:
 *   1. AuthProvider checks Firebase Auth state
 *   2. If authenticated + admin → show protected routes (Layout wrapper)
 *   3. If not authenticated → redirect to /login
 *   4. Logged-in users visiting /login → redirect to /
 *
 * Feature flags:
 *   Routes under FeatureProtectedRoute require the company to have the feature enabled.
 *   Currently: vehicles, vehicle detail, and deliveries require the "delivery" feature.
 *
 * Code splitting:
 *   All pages except Login/Layout/NotFound are lazy-loaded for faster initial bundle.
 */
import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress, Typography } from '@mui/material';
import { AuthProvider, useAuth } from './context/AuthContext';
import { FeatureProvider, useFeatures } from './context/FeatureContext';
import { DataProvider } from './context/DataContext';
import ErrorBoundary from './components/ErrorBoundary';

// Layout - imported directly as it's used on all protected routes
import Layout from './components/Layout/Layout';

// Login - imported directly as it's the entry point
import Login from './pages/Login';
import NotFound from './pages/NotFound';

// Lazy-loaded pages for code splitting
const Dashboard = lazy(() => import('./pages/Dashboard'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const Absences = lazy(() => import('./pages/Absences'));
const Vehicles = lazy(() => import('./pages/Vehicles'));
const VehicleDetail = lazy(() => import('./pages/VehicleDetail'));
const DeliveryReceipts = lazy(() => import('./pages/DeliveryReceipts'));
const TimeTracking = lazy(() => import('./pages/TimeTracking'));
const Calendar = lazy(() => import('./pages/Calendar'));
const Todos = lazy(() => import('./pages/Todos'));
const Spesen = lazy(() => import('./pages/Spesen'));

// Loading fallback component for Suspense
const PageLoader = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '50vh',
      gap: 2
    }}
  >
    <CircularProgress size={40} />
    <Typography variant="body2" color="text.secondary">
      Seite wird geladen...
    </Typography>
  </Box>
);

// Create a theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1e62d0',
    },
    secondary: {
      main: '#ff9800',
    },
    error: {
      main: '#f44336',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
  },
});

/**
 * Route guard that checks if the admin's companies have a specific feature enabled.
 * Redirects to dashboard if feature is disabled. Feature check happens at render time.
 * @see FeatureContext for how features are aggregated across companies (OR logic).
 */
function FeatureProtectedRoute({ children, requiredFeature }) {
  const { features, loading } = useFeatures();

  if (loading) {
    return <PageLoader />;
  }

  // If the user doesn't have the required feature, redirect to dashboard
  if (!features[requiredFeature]) {
    return <Navigate to="/" />;
  }

  return children;
}

function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/login" element={user ? <Navigate to="/" /> : <Login />} />

            {/* Protected Routes */}
            <Route path="/" element={user ? <Layout /> : <Navigate to="/login" />}>
              <Route index element={<Dashboard />} />
              <Route path="users" element={<UserManagement />} />
              <Route path="users/:userId" element={<UserProfile />} />
              <Route path="absences" element={<Absences />} />
              <Route path="calendar" element={<Calendar />} />
              <Route path="todos" element={<Todos />} />
              <Route path="spesen" element={<Spesen />} />

              {/* Delivery Feature Protected Routes */}
              <Route
                path="vehicles"
                element={
                  <FeatureProtectedRoute requiredFeature="delivery">
                    <Vehicles />
                  </FeatureProtectedRoute>
                }
              />
              <Route
                path="vehicles/:vehicleId"
                element={
                  <FeatureProtectedRoute requiredFeature="delivery">
                    <VehicleDetail />
                  </FeatureProtectedRoute>
                }
              />
              <Route
                path="deliveries"
                element={
                  <FeatureProtectedRoute requiredFeature="delivery">
                    <DeliveryReceipts />
                  </FeatureProtectedRoute>
                }
              />

              <Route path="time-tracking" element={<TimeTracking />} />
            </Route>

            {/* 404 Not Found */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </Router>
    </ThemeProvider>
  );
}

/**
 * Root component - sets up the context provider tree.
 * Order is critical: each provider depends on the one above it.
 *   ErrorBoundary: catches uncaught React errors
 *   AuthProvider: user auth state + admin company IDs
 *   FeatureProvider: feature flags from company docs (needs auth)
 *   DataProvider: cached users, holidays (needs auth + features)
 */
export default function Root() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <FeatureProvider>
          <DataProvider>
            <App />
          </DataProvider>
        </FeatureProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}